import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { NAV, AUTH } from "@/constants/testIds";
import FocusModeOverlay from "@/components/FocusModeOverlay";
import { AppTour, restartTour } from "@/components/AppTour";
import { PageTour, PageTourButton } from "@/components/PageTour";
import {
  LayoutGrid,
  Users,
  CalendarDays,
  CalendarClock,
  Sparkles,
  Send,
  FileText,
  Building2,
  LogOut,
  Settings as SettingsIcon,
  UserPlus2,
  Receipt,
  BarChart3,
  KeyRound,
  BookOpen,
  History,
  Zap,
  Inbox as InboxIcon,
  Moon,
  Sun,
  MonitorSmartphone,
  AlertTriangle,
  Camera,
  HelpCircle,
  Award,
} from "lucide-react";

// Session Z78 — three-state theme preference ("auto" | "light" | "dark").
// "auto" (default) honours the OS `prefers-color-scheme: dark` media query
// so late-shift operators drop into dark without flipping a switch. Manual
// picks stick in localStorage. Exported so the landlord portal can reuse
// the exact same wiring and the toggle feels identical everywhere.
export function useDarkMode() {
  const [mode, setMode] = useState(() => {
    try {
      const raw = localStorage.getItem("ns-dark-mode");
      if (raw === "auto" || raw === "light" || raw === "dark") return raw;
      // Legacy value from Z77: "1" meant dark, "0" meant light. Upgrade silently.
      if (raw === "1") return "dark";
      if (raw === "0") return "light";
      return "auto";
    } catch { return "auto"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = mode === "dark" || (mode === "auto" && mql.matches);
      root.classList.toggle("dark", dark);
    };
    apply();
    try { localStorage.setItem("ns-dark-mode", mode); } catch {}
    if (mode === "auto") {
      mql.addEventListener("change", apply);
      return () => mql.removeEventListener("change", apply);
    }
    return undefined;
  }, [mode]);

  return [mode, setMode];
}

export function DarkModeToggle({ className = "" }) {
  const [mode, setMode] = useDarkMode();
  const next = mode === "auto" ? "light" : mode === "light" ? "dark" : "auto";
  const Icon = mode === "dark" ? Sun : mode === "light" ? Moon : MonitorSmartphone;
  const label =
    mode === "auto"
      ? "Auto — follows your device"
      : mode === "light"
        ? "Light mode"
        : "Dark mode";
  return (
    <button
      type="button"
      data-testid="layout-darkmode-toggle"
      data-mode={mode}
      onClick={() => setMode(next)}
      title={`${label} · click to switch to ${next}`}
      aria-label={label}
      className={`rounded-full h-11 w-11 inline-flex items-center justify-center border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 dark:bg-slate-900/40 dark:border-slate-700 dark:text-slate-200 ${className}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  // Session Z85 — cleaners see Dashboard + Calendar + Cleaning + Properties
  // (read-only). Inbox stays off the list so cleaners don't see guest DMs.
  const CLEANER_NAV_KEYS = new Set(["/", "/calendar", "/cleaning", "/properties", "/training-guides", "/damage-reports"]);
  const allItems = [
    { to: "/", label: "Dashboard", icon: LayoutGrid, testId: NAV.dashboard, end: true },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: NAV.calendar },
    { to: "/contacts", label: "Contacts", icon: Users, testId: NAV.contacts },
    { to: "/bookings", label: "Bookings", icon: CalendarClock, testId: NAV.bookings },
    { to: "/journeys", label: "Journeys", icon: KeyRound, testId: "nav-journeys" },
    { to: "/signed-agreements", label: "Signed vault", icon: FileText, testId: "nav-signed-vault" },
    { to: "/inbox", label: "Inbox", icon: InboxIcon, testId: "nav-inbox" },
    { to: "/cleaning", label: "Cleaning", icon: Sparkles, testId: NAV.cleaning },
    { to: "/damage-reports", label: "Damage reports", icon: AlertTriangle, testId: "nav-damage-reports" },
    ...(user?.role === "cleaner" ? [] : [{ to: "/turnover-photos", label: "Turnover photos", icon: Camera, testId: "nav-turnover-photos" }]),
    ...(user?.role === "owner" || user?.role === "admin" ? [{ to: "/cleaner-quality", label: "Cleaner quality", icon: Award, testId: "nav-cleaner-quality" }] : []),
    { to: "/campaigns", label: "Campaigns", icon: Send, testId: NAV.campaigns },
    { to: "/templates", label: "Templates", icon: FileText, testId: NAV.templates },
    { to: "/properties", label: "Properties", icon: Building2, testId: NAV.properties },
    { to: "/statements", label: "Statements", icon: Receipt, testId: NAV.statements },
    ...(user?.role === "owner"
      ? [{ to: "/guest-portal", label: "Guest Portal", icon: Sparkles, testId: "nav-guest-portal" }]
      : []),
    ...(user?.role !== "cleaner"
      ? [
          { to: "/finance", label: "Finance", icon: Receipt, testId: NAV.finance },
          { to: "/compliance", label: "Compliance", icon: FileText, testId: NAV.compliance },
        ]
      : []),
    { to: "/reports", label: "Reports", icon: BarChart3, testId: NAV.reports },
    ...(user?.role === "owner"
      ? [{ to: "/team", label: "Team", icon: UserPlus2, testId: NAV.team }]
      : []),
    { to: "/training-guides", label: "Training guides", icon: BookOpen, testId: "nav-training-guides" },
    ...(user?.role !== "cleaner"
      ? [{ to: "/cost-audit", label: "Cost audit", icon: History, testId: "nav-cost-audit" }]
      : []),
    ...(user?.role === "owner"
      ? [
          { to: "/bulk-history", label: "Bulk history", icon: History, testId: "nav-bulk-history" },
          { to: "/stripe-smoke-test", label: "Stripe smoke test", icon: Zap, testId: "nav-stripe-smoke-test" },
        ]
      : []),
    { to: "/settings", label: "Settings", icon: SettingsIcon, testId: NAV.settings },
  ];
  const items = user?.role === "cleaner"
    ? allItems.filter((i) => CLEANER_NAV_KEYS.has(i.to))
    : allItems;

  return (
    <div className="min-h-screen flex bg-stone-50">
      <aside className="ns-sidebar w-72 shrink-0 text-white flex flex-col relative z-10">
        <div className="p-8 flex items-center gap-4">
          <img
            src={LOGO_URL}
            alt="Newport Stays"
            className="w-12 h-12 rounded-lg object-cover ring-1 ring-white/20"
          />
          <div className="leading-tight">
            <div className="font-serif text-xl">Newport Stays</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-teal-200/80">
              Serviced Accommodation · Newport · Bristol · Cardiff
            </div>
          </div>
        </div>

        <div className="h-px mx-8 bg-white/10" />

        <nav className="mt-6 px-4 flex-1 space-y-1">
          {items.map(({ to, label, icon: Icon, testId, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                  isActive
                    ? "bg-white/10 text-white shadow-inner"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-1 h-6 rounded-full ${isActive ? "bg-teal-300" : "bg-transparent group-hover:bg-white/20"}`}
                  />
                  <Icon className="w-4 h-4" />
                  <span className="tracking-wide">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-6 border-t border-white/10">
          <div className="flex items-center gap-3" data-testid={NAV.userMenu}>
            <div className="w-9 h-9 rounded-full bg-teal-500/20 flex items-center justify-center font-serif">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">
                {user?.role ? user.role : user?.email}
              </div>
            </div>
            <button
              data-testid="sidebar-restart-tour"
              onClick={() => restartTour()}
              className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10"
              title="Replay menu tour"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={() => {
                logout();
                nav("/login");
              }}
              className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 ns-coastal">
        <AppTour items={items} />
        <header className="px-10 pt-10 pb-6 flex items-end justify-between gap-6 flex-wrap ns-page-header">
          <div>
            <div className="ns-label mb-2 text-teal-700">Newport Stays</div>
            <h1 className="font-serif text-4xl md:text-5xl text-slate-900 tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-slate-500 max-w-2xl">{subtitle}</p>
            )}
          </div>
          {actions && <div className="flex gap-2">{actions}</div>}
          <PageTourButton />
          <DarkModeToggle />
        </header>
        <div className="px-10 pb-16">{children}</div>
        <PageTour />
      <FocusModeOverlay />
      </main>
    </div>
  );
}
